export const SOLAR_ACTIVITY_GLSL = `float angularDistance(float first, float second) {
  return abs(atan(sin(first - second), cos(first - second)));
}

float signedAngularDistance(float first, float second) {
  return atan(sin(first - second), cos(first - second));
}

float irregularSunspot(
  vec3 surfaceNormal,
  vec3 centerNormal,
  float scale,
  float distortion
) {
  float angularSeparation = acos(clamp(dot(surfaceNormal, centerNormal), -1.0, 1.0));
  return 1.0 - smoothstep(
    scale * 0.68,
    scale * 1.08,
    angularSeparation + distortion * scale * 0.32
  );
}

float solarEruption(
  float angle,
  float radialDistance,
  float time,
  float seed
) {
  const float photosphereRadius = 0.3125;
  float cycle = time * (0.012 + seed * 0.0018) + seed * 5.73;
  float eventIndex = floor(cycle);
  float age = fract(cycle);
  float eventGate = step(
    0.46,
    hash21(vec2(eventIndex + seed * 7.1, seed * 23.7))
  );
  float eventKey = hash21(vec2(eventIndex, seed * 19.31));
  float rootAngle = eventKey * 6.28318530718 - 3.14159265359;
  float width = mix(0.055, 0.16, hash21(vec2(eventIndex + 7.2, seed)));
  float reach = mix(0.07, 0.19, hash21(vec2(eventIndex - 3.4, seed + 9.1)));
  float radialProgress = clamp(
    (radialDistance - photosphereRadius) / max(reach, 0.001),
    0.0,
    1.0
  );
  float turbulence = valueNoise(vec2(
    radialProgress * 13.0 + eventIndex * 2.7,
    angle * 9.0 + seed * 17.0
  ));
  float bentRoot = rootAngle + (turbulence - 0.5) * width * 2.4 * radialProgress;
  float angularWindow = 1.0 - smoothstep(
    width * 0.28,
    width,
    angularDistance(angle, bentRoot)
  );
  float front = photosphereRadius + reach * smoothstep(0.0, 0.82, age);
  float plumeBody =
    smoothstep(photosphereRadius - 0.004, photosphereRadius + 0.015, radialDistance) *
    (1.0 - smoothstep(front - 0.018, front + 0.026, radialDistance));
  float leadingEdge = 1.0 - smoothstep(0.004, 0.018, abs(radialDistance - front));
  float filaments = 0.28 + 0.72 * pow(
    max(0.0, sin(radialProgress * 39.0 + turbulence * 9.0 + seed * 4.0)),
    2.0
  );
  float envelope =
    smoothstep(0.02, 0.16, age) * (1.0 - smoothstep(0.68, 0.98, age));
  return
    angularWindow *
    (plumeBody * filaments * 0.58 + leadingEdge) *
    envelope *
    eventGate;
}

float solarJacobsLadderFlare(float angle, float radialDistance, float time) {
  const float photosphereRadius = 0.3125;
  float cycle = time * 0.055 + 0.36;
  float eventIndex = floor(cycle);
  float age = fract(cycle);
  float eventKey = hash21(vec2(eventIndex + 4.7, 29.3));
  float rootAngle = eventKey * 6.28318530718 - 3.14159265359;
  float halfWidth = mix(
    0.11,
    0.23,
    hash21(vec2(eventIndex + 17.1, 8.4))
  );
  float signedOffset = signedAngularDistance(angle, rootAngle);
  float arcUnit = signedOffset / max(0.001, halfWidth);
  float arcWindow = 1.0 - smoothstep(0.82, 1.0, abs(arcUnit));
  float growth = smoothstep(0.03, 0.24, age);
  float split = smoothstep(0.25, 0.54, age);
  float envelope = growth * (1.0 - smoothstep(0.58, 0.74, age));
  float height = mix(
    0.045,
    0.14,
    hash21(vec2(eventIndex - 9.8, 41.2))
  ) * mix(0.18, 1.0, growth);
  float splitDirection = arcUnit < 0.0 ? -1.0 : 1.0;
  float fragmentUnit = arcUnit - splitDirection * split * 0.16;
  float arch = sqrt(max(0.0, 1.0 - fragmentUnit * fragmentUnit));
  float crownMask = smoothstep(0.34, 0.67, arch);
  float turbulence = valueNoise(vec2(
    fragmentUnit * 7.0 + eventIndex * 3.1,
    age * 8.0 + eventKey * 17.0
  ));
  float ripple = (turbulence - 0.5) * 0.013 +
    sin(arcUnit * 37.0 + age * 9.0) * 0.0024;
  float fragmentLift = split * (0.014 + abs(arcUnit) * 0.025);
  float arcRadius =
    photosphereRadius + arch * height + fragmentLift + fragmentUnit * height * 0.11 + ripple;
  float primaryFilament =
    1.0 - smoothstep(0.004, 0.014, abs(radialDistance - arcRadius));
  float secondaryFilament =
    1.0 - smoothstep(
      0.003,
      0.010,
      abs(radialDistance - arcRadius - 0.011 + (turbulence - 0.5) * 0.006)
    );
  float splitWidth = split * 0.24;
  float centerBreak = 1.0 - split * (
    1.0 - smoothstep(splitWidth, splitWidth + 0.12, abs(arcUnit))
  );
  float shreddedEdge = smoothstep(0.18, 0.72, turbulence +
    sin(arcUnit * 29.0 - age * 7.0) * 0.16);
  float fragments =
    primaryFilament * (0.58 + shreddedEdge * 0.42) +
    secondaryFilament * shreddedEdge * split * 0.46;
  return fragments * arcWindow * crownMask * centerBreak * envelope;
}

`;
